import { useCallback, useEffect, useRef, useState } from "react";

const POLL_MS      = 30_000;
const TIMEOUT_MS   = 10_000;

/**
 * useMrmsManifest — independent MRMS manifest polling.
 *
 * Activates when `selectedProduct === "mrms"` and tears down when it isn't.
 * Preserves the original AbortController / stale-request-counter / retry
 * behaviour from StormAtlasRadarPanel exactly.
 *
 * @param {string}   selectedProduct  Current radar product id.
 * @param {string}   apiBase          API root URL.
 * @param {function} [onActivate]     Called once when MRMS becomes the active
 *                                    product (before the first fetch).  The
 *                                    panel uses this to resume playback.
 * @returns {{ manifest, error, loading, retry }}
 */
export default function useMrmsManifest(selectedProduct, apiBase, onActivate) {
  const [manifest, setManifest] = useState(null);
  const [error, setError]       = useState(null);
  const [loading, setLoading]   = useState(false);

  const abortRef  = useRef(null);
  const timerRef  = useRef(null);
  const reqRef    = useRef(0);   // incremented per fetch; stale completions bail before setState
  const fetchRef  = useRef(null); // stable reference for retry

  useEffect(() => {
    if (selectedProduct !== "mrms") {
      abortRef.current?.abort();
      clearInterval(timerRef.current);
      setManifest(null);
      setError(null);
      setLoading(false);
      return;
    }

    // Notify the consumer that MRMS just became active (e.g. resume playback).
    onActivate?.();

    setLoading(true);

    const fetchMrms = async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      reqRef.current  += 1;
      const reqId = reqRef.current;
      const timeout = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
      try {
        const res = await fetch(`${apiBase}/radar/manifest?product=mrms`, {
          signal: ctrl.signal, cache: "no-store",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        // Discard if a newer request has already resolved or product changed.
        if (reqId !== reqRef.current) return;
        setManifest(data);
        setError(null);
      } catch (err) {
        if (reqId !== reqRef.current) return;
        if (err.name === "AbortError") {
          setError("Request timed out");
        } else {
          setError(err.message || "Failed to load MRMS");
        }
      } finally {
        clearTimeout(timeout);
        if (reqId === reqRef.current) setLoading(false);
      }
    };

    fetchRef.current = fetchMrms;
    fetchMrms();
    timerRef.current = setInterval(fetchMrms, POLL_MS);
    return () => { clearInterval(timerRef.current); abortRef.current?.abort(); };
  }, [selectedProduct, apiBase, onActivate]);

  const retry = useCallback(() => {
    setError(null);
    setLoading(true);
    fetchRef.current?.();
  }, []);

  return { manifest, error, loading, retry };
}
